/** @jsx jsx */
import React, { useState } from "react";

import { jsx } from "@emotion/core";

import { useHistory, Redirect } from "react-router-dom";

import { Typography, Button } from "antd";

import { Formik, Form } from "formik";

//Hooks
import useAuth from "../../hooks/auth/useAuth";
import useLoggedInToken from "../../helperHooks/useLoggedIn";
import useAPI from "../../helperHooks/useAPI";
import useEligibilityMetaData from "../../hooks/auth/useEligibilityMetaData";

import {
  LoginPageRequest,
  GstinListResponse
} from "../../services/authService/AuthService.type";

//Components
import AuthLayout from "../../components/authLayout";
import OTP from "../../components/OTP/OTP";
import { FormDropdown } from "../../components/formHelpers";

import { LoginStyles } from "./LoginStyles";
import {
  LoginContents as CONSTANTS,
  LoginValidationSchema
} from "./LoginConstants";
import CustomButton from "../../components/button";
import useRedirect from "../../helperHooks/useRedirect";
import authService from "../../services/authService/AuthService";

import { getEligibilityRoute } from "../../hooks/eligibility/useEligibilityRouting";

const { Text } = Typography;

const initialState = {
  phoneNumber: "",
  gstin: "",
  isPhoneVerified: false
};

const Login: React.FC = () => {
  const {
    login,
    setLoginIssuePhoneNumber,
    loginState: { loading: loggingIn }
  } = useAuth();
  const loggedIn = useLoggedInToken();

  const history = useHistory();
  const redirect = useRedirect();

  const {
    eligibilityMetaData: { loading: loadingMetaData, status }
  } = useEligibilityMetaData();

  const { callAPI: getGstinList, loading: gstinListLoading } = useAPI(
    authService.getGstinsList
  );

  const [gstinList, setGstinList] = useState<string[]>();

  const loading = loggingIn || loadingMetaData;

  const getGstins = (phoneNumber: string) => {
    getGstinList(phoneNumber, (gstinListResponse?: GstinListResponse) => {
      if (!gstinListResponse) {
        return;
      }
      setGstinList(gstinListResponse.registeredGstins.map(item => item.gstin));
    });
  };

  const redirectTo = (route: string, reLoggingIn: boolean) => {
    const routedFrom: any = history.location.pathname;
    if (routedFrom && typeof routedFrom === "string" && reLoggingIn) {
      redirect(routedFrom);
    } else {
      history.replace(route);
    }
  };

  const handleReportIssueClick = (phoneNumber: string) => {
    setLoginIssuePhoneNumber(phoneNumber);
    redirect(routes => routes.reportIssue.new);
  };

  const handleSubmitFunction = (values: LoginPageRequest) => {
    const { phoneNumber, gstin } = values;
    login({ phoneNumber, gstin }, redirectTo);
  };

  const renderLoginForm = (
    <AuthLayout>
      <div css={LoginStyles} className="mx-auto">
        <Text className="title">
          {CONSTANTS.titles.heading}
          <span className="title-underline d-block" />
        </Text>
        <Formik
          initialValues={initialState}
          onSubmit={handleSubmitFunction}
          validationSchema={LoginValidationSchema}
          validateOnChange
        >
          {({ handleChange, values, errors, resetForm, setFieldValue }) => (
            <Form>
              <OTP
                className="mt-4"
                label={CONSTANTS.labels.phoneNumber}
                placeholder={CONSTANTS.placeholders.phoneNumber}
                onReset={() => {
                  //empty states and reset form
                  resetForm();
                  setGstinList([]);
                }}
                handleChange={handleChange}
                isValid={!!errors.phoneNumber}
                phoneNumber={values.phoneNumber}
                onVerifySuccess={() => {
                  setFieldValue("isPhoneVerified", true);
                  getGstins(values.phoneNumber);
                }}
                register={false}
              />
              {values.isPhoneVerified && (
                <React.Fragment>
                  <FormDropdown
                    name={CONSTANTS.fields.gstin}
                    className="mt-4"
                    options={gstinList || []}
                    label={CONSTANTS.labels.gstin}
                    placeholder={CONSTANTS.placeholders.gstin}
                    loading={gstinListLoading}
                    mandatory
                  />
                  <CustomButton
                    htmlType="submit"
                    containerDivClassName="justify-content-center mt-4"
                    type="primary"
                    disabled={loading}
                    loading={loading}
                  >
                    {CONSTANTS.labels.login}
                  </CustomButton>
                </React.Fragment>
              )}
              <div className="d-flex justify-content-between mt-4">
                <Text>
                  {CONSTANTS.labels.registrationPrefix}
                  <Button
                    className="pl-0"
                    type="link"
                    onClick={() => {
                      redirect(routes => routes.registration.new);
                    }}
                  >
                    {CONSTANTS.labels.register}
                  </Button>
                </Text>
                <Button
                  className="px-0"
                  type="link"
                  onClick={() => handleReportIssueClick(values.phoneNumber)}
                >
                  {CONSTANTS.labels.reportIssue}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </AuthLayout>
  );

  const redirectOnLoggedIn = () => {
    const [route] = getEligibilityRoute(status);

    return <Redirect to={route} />;
  };

  // if login API is being called or metaData is being fetched don't redirect
  return !loggedIn || loading ? renderLoginForm : redirectOnLoggedIn();
};

export default Login;
